import { useState, useEffect } from 'react';
import { IconTruck, IconMail, IconPrinter, IconX, IconCheck, IconBan, IconSend } from '@tabler/icons-react';
import { fetchOrders, updateOrder, sendOrderEmail } from '../lib/storeData';
import { showToast } from '../components/ui/Toast';

export default function AdminOrders() {
  var [orders, setOrders] = useState([]);
  var _filter = useState('all');
  var filter = _filter[0];
  var setFilter = _filter[1];
  var _detail = useState(null);
  var detail = _detail[0];
  var setDetail = _detail[1];
  var [loading, setLoading] = useState(true);
  var [loadError, setLoadError] = useState(null);
  var [busy, setBusy] = useState(false);

  useEffect(function() {
    var cancelled = false;
    fetchOrders().then(function(res) {
      if (cancelled) return;
      setOrders(res.orders);
      setLoadError(res.error);
      setLoading(false);
    });
    return function() { cancelled = true; };
  }, []);

  var filtered = filter === 'all' ? orders : orders.filter(function(o) { return o.status === filter; });

  var stats = {
    revenue: orders.filter(function(o) { return o.status !== 'cancelled'; }).reduce(function(sum, o) { return sum + (o.total || 0); }, 0),
    total: orders.length,
    awaiting: orders.filter(function(o) { return o.status === 'paid' || o.status === 'processing'; }).length,
    delivered: orders.filter(function(o) { return o.status === 'delivered'; }).length,
  };

  // The tracking-number box under an order, shown after "Mark shipped".
  var [shipForm, setShipForm] = useState(null); // null | { orderId, tracking }

  function openOrder(o) {
    setDetail(o);
    setShipForm(null);
  }

  function replaceOrder(order) {
    setOrders(function(prev) {
      return prev.map(function(o) { return o.id === order.id ? order : o; });
    });
    // Only refresh the open panel if it still shows this order (Andre may
    // have closed it or opened another while the save was running).
    setDetail(function(prev) { return prev && prev.id === order.id ? order : prev; });
  }

  async function updateStatus(order, newStatus, extra) {
    if (busy) return null;
    var changes = Object.assign({ status: newStatus }, extra || {});
    if (newStatus === 'cancelled') {
      if (!window.confirm('Cancel this order?\n\nThis does NOT refund the customer. Refund them in your Stripe dashboard as well.')) return null;
    }
    setBusy(true);
    var res = await updateOrder(order.id, changes);
    setBusy(false);
    if (res.error) {
      showToast(res.error, 'error');
      return null;
    }
    replaceOrder(res.order);
    if (newStatus !== 'shipped') showToast('Order ' + res.order.order_number + (newStatus === 'delivered' ? ' marked as delivered' : ' updated'));
    return res.order;
  }

  // Email the customer that their bag is on its way. Safe to click twice:
  // the shipping email only ever goes once per order.
  async function emailShipping(order, justShipped) {
    setBusy(true);
    var res = await sendOrderEmail(order.id, 'shipping');
    setBusy(false);
    var prefix = justShipped ? 'Order ' + order.order_number + ' is marked as shipped' : '';
    if (res.error) {
      showToast((prefix ? prefix + ', but the' : 'The') + ' shipping email wasn’t sent: ' + res.error + ' Click “Send shipping email” to try again.', 'error');
      return;
    }
    if (res.alreadySent) {
      showToast((prefix ? prefix + '. The' : 'The') + ' shipping email was already sent to ' + (res.sentTo || order.guest_email) + ' earlier.');
      return;
    }
    showToast((prefix ? prefix + '. ' : '') + 'Shipping email sent to ' + (res.sentTo || order.guest_email) + '.');
  }

  async function confirmShipped(order) {
    var tracking = ((shipForm && shipForm.tracking) || '').trim();
    if (!tracking && !window.confirm('Ship without a tracking number?\n\nThe shipping email will say tracking isn’t available yet.')) return;
    var saved = await updateStatus(order, 'shipped', { tracking_number: tracking });
    if (!saved) return;
    setShipForm(null);
    await emailShipping(saved, true);
  }

  function emailLink(order) {
    var subject = 'Your Andre M Leather order ' + order.order_number;
    return 'mailto:' + encodeURIComponent(order.guest_email || '').replace(/%40/g, '@') + '?subject=' + encodeURIComponent(subject);
  }

  var statusColors = {
    paid: { bg: '#EEF3FF', color: '#2D4DB5' },
    processing: { bg: '#FFF8E6', color: '#8B6000' },
    shipped: { bg: '#E6F4EC', color: '#1E6B3A' },
    delivered: { bg: '#F0F0F0', color: '#555' },
    cancelled: { bg: '#FDE8E8', color: '#8B2020' },
  };

    if (loading) {
    return <p className="adm-sub">Loading orders...</p>;
  }

  return (
    <div>
      <h1 className="adm-title">Orders</h1>
      <p className="adm-sub">Only paid orders appear here. Only start making a bag once its order shows here as paid. Click an order to manage it.</p>
      {loadError && (
        <div className="adm-card" style={{ color: '#8B2020', fontSize: 12 }}>
          Could not load orders. Check your internet connection and refresh the page.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
        {[
          { label: 'Total revenue', value: '$' + stats.revenue.toFixed(0), sub: 'All time' },
          { label: 'Orders', value: stats.total, sub: 'Total' },
          { label: 'Awaiting shipment', value: stats.awaiting, sub: stats.awaiting > 0 ? 'Action needed' : 'All clear' },
          { label: 'Delivered', value: stats.delivered, sub: 'All time' },
        ].map(function(s, i) {
          return (
            <div className="adm-card" key={i} style={{ marginBottom: 0 }}>
              <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{s.label}</div>
              <div style={{ fontSize: 22, fontWeight: 500, color: '#1A1A1A' }}>{s.value}</div>
              <div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>{s.sub}</div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {['all','paid','processing','shipped','delivered','cancelled'].map(function(f) {
          return (
            <button key={f} className={'adm-btn' + (filter === f ? '' : ' adm-btn--outline')} onClick={function() { setFilter(f); }} style={{ textTransform: 'capitalize', padding: '5px 12px', fontSize: 11 }}>
              {f === 'all' ? 'All orders' : f}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="adm-card" style={{ textAlign: 'center', padding: 32 }}>
          <p style={{ fontSize: 13, color: '#888' }}>No orders to show.</p>
        </div>
      ) : (
        <div className="adm-card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'rgba(74,46,26,0.04)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#888' }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500 }}>Order</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500 }}>Customer</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500 }}>Amount</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500 }}>Status</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500 }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(function(o) {
                var sc = statusColors[o.status] || statusColors.paid;
                return (
                  <tr key={o.id} onClick={function() { openOrder(o); }} style={{ borderBottom: '1px solid rgba(74,46,26,0.08)', cursor: 'pointer' }}>
                    <td style={{ padding: '12px 14px', fontFamily: 'monospace', color: 'var(--color-primary)', fontWeight: 500 }}>{o.order_number + (o.admin_notes ? ' \u26a0' : '')}</td>
                    <td style={{ padding: '12px 14px' }}>{o.guest_name || 'Customer'}</td>
                    <td style={{ padding: '12px 14px', fontWeight: 500, color: 'var(--color-secondary)' }}>{'$' + o.total}</td>
                    <td style={{ padding: '12px 14px' }}><span style={{ fontSize: 10, padding: '3px 9px', borderRadius: 20, background: sc.bg, color: sc.color, textTransform: 'capitalize' }}>{o.status}</span></td>
                    <td style={{ padding: '12px 14px', color: '#888' }}>{new Date(o.created_at).toLocaleDateString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <div className="adm-card" style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{'Order ' + detail.order_number}</div>
              <div style={{ fontSize: 11, color: '#888' }}>
                {'Status: ' + detail.status
                  + (detail.tracking_number ? ' \u2022 Tracking: ' + detail.tracking_number : '')
                  + (detail.delivered_at ? ' \u2022 Delivered ' + new Date(detail.delivered_at).toLocaleDateString() : '')}
              </div>
            </div>
            <button aria-label="Close order" onClick={function() { setDetail(null); setShipForm(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}><IconX size={18} /></button>
          </div>

          {detail.admin_notes && (
            <div style={{ background: '#FFF8E6', border: '1px solid rgba(139,96,0,0.3)', color: '#8B6000', borderRadius: 6, padding: '10px 12px', fontSize: 12, marginBottom: 14, lineHeight: 1.5 }}>
              <strong>{'Please check: '}</strong>{detail.admin_notes}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Customer</div>
              <div style={{ fontSize: 12, color: '#333', lineHeight: 1.6 }}>
                {detail.guest_name}<br />{detail.guest_email}{detail.phone ? <><br />{detail.phone}</> : null}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Ship to</div>
              <div style={{ fontSize: 12, color: '#333', lineHeight: 1.6 }}>
                {detail.shipping_street}{detail.shipping_apt ? ', ' + detail.shipping_apt : ''}<br />
                {detail.shipping_city}{detail.shipping_state ? ', ' + detail.shipping_state : ''} {detail.shipping_zip}<br />
                {detail.shipping_country}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Payment</div>
              <div style={{ fontSize: 12, color: '#333', lineHeight: 1.6 }}>
                {'Subtotal: $' + (detail.subtotal || 0)}<br />
                {'Shipping: $' + (detail.shipping_cost || 0)}<br />
                {detail.discount_amount > 0 ? <>{'Discount: -$' + detail.discount_amount}<br /></> : null}
                <strong>{'Total: $' + detail.total}</strong>
              </div>
            </div>
          </div>

          {detail.items && detail.items.length > 0 && (
            <div style={{ borderTop: '1px solid rgba(74,46,26,0.08)', paddingTop: 12, marginBottom: 12 }}>
              {detail.items.map(function(item, i) {
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0' }}>
                    <span>{item.name + ' ' + (item.variant || '') + ' ' + (item.colour || '') + ' x' + item.quantity}</span>
                    <span style={{ fontWeight: 500 }}>{'$' + (item.price * item.quantity).toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
          )}

          {shipForm && shipForm.orderId === detail.id && detail.status === 'processing' && (
            <form
              onSubmit={function(e) { e.preventDefault(); confirmShipped(detail); }}
              style={{ background: '#F4F8F5', border: '1px solid rgba(30,107,58,0.25)', borderRadius: 6, padding: 12, marginBottom: 12 }}
            >
              <label htmlFor="ship-tracking" style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Tracking number</label>
              <input
                id="ship-tracking"
                className="adm-input"
                autoFocus
                maxLength={200}
                placeholder="e.g. LX123456789GY, or paste the tracking link"
                value={shipForm.tracking}
                onChange={function(e) { setShipForm({ orderId: detail.id, tracking: e.target.value }); }}
                style={{ width: '100%', marginBottom: 8 }}
              />
              <div style={{ fontSize: 11, color: '#666', marginBottom: 10 }}>
                {'Saving marks the order as shipped and emails ' + (detail.guest_email || 'the customer') + ' the shipping email with this tracking number.'}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="adm-btn" disabled={busy}>
                  <IconTruck size={14} /> {busy ? 'Saving…' : 'Mark shipped & email customer'}
                </button>
                <button type="button" className="adm-btn adm-btn--outline" onClick={function() { setShipForm(null); }}>Cancel</button>
              </div>
            </form>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 12, borderTop: '1px solid rgba(74,46,26,0.08)' }}>
            {detail.status === 'paid' && (
              <button className="adm-btn" disabled={busy} onClick={function() { updateStatus(detail, 'processing'); }}>
                <IconCheck size={14} /> Mark processing
              </button>
            )}
            {detail.status === 'processing' && !(shipForm && shipForm.orderId === detail.id) && (
              <button className="adm-btn" disabled={busy} onClick={function() { setShipForm({ orderId: detail.id, tracking: detail.tracking_number || '' }); }}>
                <IconTruck size={14} /> Mark shipped
              </button>
            )}
            {detail.status === 'shipped' && (
              <button className="adm-btn" disabled={busy} onClick={function() { updateStatus(detail, 'delivered'); }}>
                <IconCheck size={14} /> Mark delivered
              </button>
            )}
            {(detail.status === 'shipped' || detail.status === 'delivered') && (
              <button className="adm-btn adm-btn--outline" disabled={busy} onClick={function() { emailShipping(detail, false); }}>
                <IconSend size={14} /> Send shipping email
              </button>
            )}
            {detail.status !== 'cancelled' && detail.status !== 'delivered' && detail.status !== 'shipped' && (
              <button className="adm-btn adm-btn--danger" disabled={busy} onClick={function() { updateStatus(detail, 'cancelled'); }}>
                <IconBan size={14} /> Cancel order
              </button>
            )}
            <button className="adm-btn adm-btn--outline">
              <IconPrinter size={14} /> Print label
            </button>
            {detail.guest_email && (
              <a className="adm-btn adm-btn--outline" href={emailLink(detail)} style={{ textDecoration: 'none' }}>
                <IconMail size={14} /> Email customer
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}